import { AppLink, formatDateTime, Panel } from "@amber/ui";
import type { BudgetsResponse } from "../../contracts/index";
import { num, usd } from "./money";
import { Name, Table, Td, Th } from "./parts";

export function PolicyPanel({ data }: { data: BudgetsResponse }) {
  const categories = data.allowances.filter((a) => a.key !== "total");
  return (
    <Panel title="Essential-service policy" note="when a category or the total reaches its allowance">
      <Table
        label="Essential-service policy"
        minWidth={560}
        head={
          <>
            <Th className="w-[44%]">Work</Th>
            <Th>When the allowance is reached</Th>
          </>
        }
      >
        {data.essential_policy.map((p) => (
          <tr key={p.work}>
            <Td>
              <Name detail={p.detail}>{p.work}</Name>
            </Td>
            <Td className="text-fg">{p.treatment}</Td>
          </tr>
        ))}
      </Table>
      <div className="mt-2.5 text-ui">
        <span className="text-muted">Reserve: {data.reserve_percent}% of each category allowance</span>
        <ul className="m-0 mt-1 flex list-none flex-wrap gap-x-5 gap-y-1 p-0">
          {categories.map((c) => (
            <li key={c.key}>
              <b className="font-strong">{c.name}</b>{" "}
              <span className="text-muted">
                Reserve: {usd(c.reserve.allowance)} ·{" "}
                {num(c.reserve.used) === 0 ? "none used" : `${usd(c.reserve.used)} used`}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <h3 className="m-0 mt-3.5 mb-1.5 text-base font-semibold">Overruns and reserve use</h3>
      <Table
        label="Overruns and reserve use"
        minWidth={560}
        head={
          <>
            <Th>When</Th>
            <Th>Category</Th>
            <Th right>Amount</Th>
            <Th>What continued and what waited</Th>
          </>
        }
      >
        {data.reserve_log.length === 0 ? (
          <tr>
            <Td colSpan={4}>No overruns or reserve use this month</Td>
          </tr>
        ) : null}
        {data.reserve_log.map((r) => (
          <tr key={`${r.at}-${r.category}-${r.what}`}>
            <Td className="whitespace-nowrap">{formatDateTime(r.at)}</Td>
            <Td className="text-fg">{r.category}</Td>
            <Td right className="tabular-nums text-fg">
              {usd(r.amount)}
            </Td>
            <Td className="text-fg">
              {r.what}
              {r.task_id ? (
                <span className="block text-sm">
                  <AppLink href={`/tasks/${r.task_id}`} className="font-strong text-link">
                    {r.open ? "Open budget task" : "Budget task, closed"}
                  </AppLink>
                </span>
              ) : null}
            </Td>
          </tr>
        ))}
      </Table>
      <p className="m-0 mt-2.5 max-w-[780px] text-ui text-muted">
        Every overrun and use of the reserve is listed here and opens a budget task for Admins. Only Admins change
        allowances, the reserve or this policy.
      </p>
    </Panel>
  );
}
